import { useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Table,
  TextInput,
  Button,
  Paper,
  Group,
  Text,
  Badge,
  Loader,
  Stack,
  Divider,
  ActionIcon,
  Tabs,
  Tooltip,
  VisuallyHidden,
  Alert,
  Checkbox,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconRefresh,
  IconFilter,
  IconSearch,
  IconCircleCheck,
  IconAlertTriangle,
  IconStethoscope,
  IconMessageCircle,
  IconSettings,
  IconFlag,
  IconSortAscending,
  IconSortDescending,
  IconScissors,
  IconCube,
  IconCut,
  IconDroplet,
  IconSend,
  IconTestPipe,
  IconFlask,
  IconX,
  IconInbox,
  IconLock,
  IconUsers,
  IconAlertCircle,
} from "@tabler/icons-react";
import { QcSlideReviewQueue } from "../../components/pathologist-review/QcSlideReviewQueue";
import {
  useInfinitePathologistReviewQueue,
  usePathologistReviewPrefixOptions,
  usePathologistReviewStats,
  useInfinitePathologistIncomingQueue,
  useInfinitePathologistCompletedCases,
  useOpenCaseByBarcode,
  useUserPreference,
  useSaveUserPreference,
} from "../../hooks/useWorkflows";
import { useInfiniteCorrectionRequestsWorkQueue } from "../../hooks/useCorrectionRequests";
import { FilterModal, ActiveFilters } from "../../components/cases";
import { useAuthStore } from "../../store/auth.store";
import { QueuePreferencesModal } from "../../components/pathologist-review/QueuePreferencesModal";
import { IncomingPreferencesModal } from "../../components/pathologist-review/IncomingPreferencesModal";
import { ColumnTooltip } from "../../components/pathologist-review/ColumnTooltip";
import {
  SummaryBar,
  type SummaryFilterType,
} from "../../components/pathologist-review/SummaryBar";
import { type CaseFilters } from "../../services";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import {
  ALL_QUEUE_COLUMNS,
  DEFAULT_QUEUE_PREFERENCES,
  PREFERENCE_KEY,
  ALL_INCOMING_COLUMNS,
  DEFAULT_INCOMING_PREFERENCES,
  INCOMING_PREFERENCE_KEY,
  type QueueColumnDef,
  type QueuePreferences,
  type IncomingQueuePreferences,
  type PathologistReviewQueueCase,
  type IncomingLabCase,
  SEND_OUT_STATUS_COLUMN,
} from "./queueColumnConfig";
import { AllPathologistCasesTab } from "./AllPathologistCasesTab";
import { CorrectionRequestsTab } from "./CorrectionRequestsTab";
import {
  buildAllReviewColumns,
  buildAssignedReviewColumns,
  buildAssignedPathologistReviewQueueFilters,
  getPathologistReviewRowAppearance,
  advanceReviewSorts,
  type QueueSortState,
} from "./pathologistReviewQueueView";
import { isMedicalDirector } from "../../utils/medicalDirectorAccess";
import { TcReviewCompletionButton } from "../../components/pathologist-review/TcReviewCompletionButton";
import { PathologistQueueColorLegend } from "./PathologistQueueColorLegend";
import { PathologistQueueStateBadge } from "./PathologistQueueStateBadge";
import { usePathologistQueueViewPreference } from "../../hooks/usePathologistQueueViewPreference";
import { usePathologistQueueDefaultPreference } from "../../hooks/usePathologistQueueDefaultPreference";
import {
  applyPathologistQueueDefault,
  createPathologistQueueDefault,
  decodePathologistQueueDefault,
} from "./pathologistQueueDefaultPreference";
import {
  getPathologistQueueTabView,
  type PathologistQueueTabView,
  type PathologistQueueViewTab,
} from "./pathologistQueueViewPreference";
import { createPathologistQueueReturnState } from "./pathologistQueueNavigation";
import {
  calendarDayDifference,
  formatDateOnlyForDisplay,
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";
import {
  formatLabCalendarDate,
  formatLabClockTime24,
} from "../../utils/labTime";

const WORKFLOW_LABELS: Record<string, string> = {
  global: "Global",
  pass_through: "Pass Through",
  consultation: "Consultation",
  tc_only: "TC Only",
  additional_stain: "Additional Stain",
};

const INCOMING_STEP_COLORS: Record<string, string> = {
  "Pending Grossing": "orange",
  Grossing: "orange",
  "Pending Tissue Processing": "grape",
  Embedding: "grape",
  "Pending Cutting": "blue",
  Cutting: "blue",
  "Pending Staining": "teal",
  Staining: "teal",
  "Pending Distribution": "cyan",
  "Awaiting Scan": "teal",
  "Awaiting Send-Out Report": "violet",
  "Awaiting Stains": "yellow",
};

const VALID_TABS = [
  "qc",
  "review",
  "incoming",
  "sentOut",
  "all",
  "corrections",
  "completed",
] as const;

const VALID_FILTERS: SummaryFilterType[] = [
  "total",
  "new",
  "stat",
  "overdue",
  "pendingStains",
  "discussion",
  "malignancy",
  "qaReview",
];

const VALID_STEPS = [
  "grossing",
  "embedding",
  "cutting",
  "staining",
  "awaiting_send_out",
  "pending_stains",
] as const;

function formatQueueDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

function getQueueTurnaroundDays(
  dateReceived: unknown,
  labToday: DateOnlyString
): number {
  const receivedDate = parseDateOnly(dateReceived);
  return receivedDate ? calendarDayDifference(receivedDate, labToday) : 0;
}

function getAgeOnDate(
  dateOfBirth: unknown,
  labToday: DateOnlyString
): number | null {
  const birthDate = parseDateOnly(dateOfBirth);
  if (!birthDate) return null;

  const [birthYear, birthMonth, birthDay] = birthDate.split("-").map(Number);
  const [todayYear, todayMonth, todayDay] = labToday.split("-").map(Number);
  const birthdayHasOccurred =
    todayMonth > birthMonth ||
    (todayMonth === birthMonth && todayDay >= birthDay);
  return todayYear - birthYear - (birthdayHasOccurred ? 0 : 1);
}

function formatQueueInstant(value: string): string {
  return `${formatLabCalendarDate(value)} ${formatLabClockTime24(value)}`;
}

export default function PathologistWorkQueuePage() {
  const navigate = useNavigate();
  const reviewBoundaryObserver = useRef<IntersectionObserver | null>(null);
  const blockedBoundaryObserver = useRef<IntersectionObserver | null>(null);
  const incomingBoundaryObserver = useRef<IntersectionObserver | null>(null);
  const completedBoundaryObserver = useRef<IntersectionObserver | null>(null);
  const labToday = useMemo(() => getLabTodayDateOnly(), []);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [defaultMessage, setDefaultMessage] = useState<string | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [incomingDefaultMessage, setIncomingDefaultMessage] = useState<
    string | null
  >(null);
  const [incomingDefaultError, setIncomingDefaultError] = useState<
    string | null
  >(null);
  const [incomingDefaultSaving, setIncomingDefaultSaving] = useState(false);
  const [completedDefaultMessage, setCompletedDefaultMessage] = useState<
    string | null
  >(null);
  const [completedDefaultError, setCompletedDefaultError] = useState<
    string | null
  >(null);
  const [completedDefaultSaving, setCompletedDefaultSaving] = useState(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    incomingFilterModalOpened,
    { open: openIncomingFilterModal, close: closeIncomingFilterModal },
  ] = useDisclosure(false);

  const authProfile = useAuthStore((state) => state.profile);
  const currentUserId = authProfile?.id;
  const currentUserRoleName = authProfile?.roleName;
  // Completed cases remain a pathologist-only personal view.
  const isPathologist = currentUserRoleName === "Pathologist";
  const hasMedicalDirectorAccess = useMemo(
    () => isMedicalDirector(authProfile),
    [authProfile]
  );
  const canEditAnyPathologistReview = useMemo(
    () =>
      authProfile?.permissions?.includes("pathologist_review.edit_any") ===
      true,
    [authProfile?.permissions]
  );
  const pathologistIdForFilter = currentUserId;
  const {
    preference: queueViewPreference,
    invalidTabs,
    isHydrated: queueViewHydrated,
    updateTab: updateQueueViewTab,
    clearTab: clearQueueViewTab,
  } = usePathologistQueueViewPreference();
  const {
    rawDefault,
    rawDefaults,
    hydratedTabs,
    errorTabs,
    isHydrated: defaultHydrated,
    isError: defaultLoadError,
    saveDefault,
  } = usePathologistQueueDefaultPreference();
  const reviewView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "review"),
    [queueViewPreference]
  );
  const incomingView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "incoming"),
    [queueViewPreference]
  );
  const allView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "all"),
    [queueViewPreference]
  );
  const sentOutView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "sentOut"),
    [queueViewPreference]
  );
  const completedView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "completed"),
    [queueViewPreference]
  );
  const correctionsView = useMemo(
    () => getPathologistQueueTabView(queueViewPreference, "corrections"),
    [queueViewPreference]
  );
  const filters = reviewView.filters;

  // Strip client-side-only fields before sending to backend
  const backendFilters = useMemo(
    () => buildAssignedPathologistReviewQueueFilters(filters),
    [filters]
  );

  const { data: eligiblePrefixKeys, isError: prefixOptionsError } =
    usePathologistReviewPrefixOptions(
      currentUserId,
      queueViewHydrated && !invalidTabs.has("review")
    );
  const { data: allEligiblePrefixKeys } = usePathologistReviewPrefixOptions(
    currentUserId,
    hasMedicalDirectorAccess && queueViewHydrated && !invalidTabs.has("all"),
    { queueScope: "all" }
  );
  const { data: sentOutEligiblePrefixKeys } = usePathologistReviewPrefixOptions(
    currentUserId,
    queueViewHydrated && !invalidTabs.has("sentOut"),
    { queueScope: "assigned", partition: "waiting" }
  );
  const prefixOptions = useMemo(
    () => eligiblePrefixKeys ?? [],
    [eligiblePrefixKeys]
  );
  const savedDefault = useMemo(
    () => decodePathologistQueueDefault(rawDefault, eligiblePrefixKeys),
    [rawDefault, eligiblePrefixKeys]
  );
  const { data: stats } = usePathologistReviewStats(pathologistIdForFilter);
  const openCaseMutation = useOpenCaseByBarcode();

  const { data: correctionCountData } =
    useInfiniteCorrectionRequestsWorkQueue();
  const correctionCount = correctionCountData?.pages[0]?.total ?? 0;

  // Active tab driven by ?tab= query param
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const requestedTab: string = VALID_TABS.includes(
    rawTab as (typeof VALID_TABS)[number]
  )
    ? (rawTab as string)
    : "review";
  const activeTab =
    requestedTab === "all" && !hasMedicalDirectorAccess
      ? "review"
      : requestedTab;
  const queueReturnTab: PathologistQueueViewTab =
    activeTab === "incoming" ||
    activeTab === "sentOut" ||
    activeTab === "all" ||
    activeTab === "completed" ||
    activeTab === "corrections"
      ? activeTab
      : "review";
  const queueReturnState = useMemo(
    () => createPathologistQueueReturnState(queueReturnTab),
    [queueReturnTab]
  );
  const activeViewInvalid =
    (activeTab === "review" ||
      activeTab === "incoming" ||
      activeTab === "sentOut" ||
      activeTab === "all" ||
      activeTab === "completed" ||
      activeTab === "corrections") &&
    invalidTabs.has(activeTab);
  const setActiveTab = useCallback(
    (tab: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab && VALID_TABS.includes(tab as (typeof VALID_TABS)[number]))
            next.set("tab", tab);
          else next.delete("tab");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const rawFilter = searchParams.get("filter");
  const summaryFilter: SummaryFilterType | null = VALID_FILTERS.includes(
    rawFilter as SummaryFilterType
  )
    ? (rawFilter as SummaryFilterType)
    : (reviewView.summaryFilter as SummaryFilterType | null) ?? null;

  // Incoming stage chip filter driven by ?step= query param
  const rawStep = searchParams.get("step");
  const incomingStep = VALID_STEPS.includes(
    rawStep as (typeof VALID_STEPS)[number]
  )
    ? (rawStep as string)
    : incomingView.filters.currentStep;
  const incomingFilters = useMemo(
    () => ({ ...incomingView.filters, currentStep: incomingStep }),
    [incomingStep, incomingView.filters]
  );

  const setIncomingStep = useCallback(
    (step: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (step) next.set("step", step);
          else next.delete("step");
          return next;
        },
        { replace: true }
      );
      updateQueueViewTab("incoming", (current) => ({
        ...current,
        filters: { ...current.filters, currentStep: step },
        page: 1,
      }));
    },
    [setSearchParams, updateQueueViewTab]
  );

  const setSummaryFilter = useCallback(
    (value: SummaryFilterType | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("filter", value);
          else next.delete("filter");
          return next;
        },
        { replace: true }
      );
      updateQueueViewTab("review", (current) => ({
        ...current,
        summaryFilter: value,
        page: 1,
      }));
    },
    [setSearchParams, updateQueueViewTab]
  );

  const handleSummaryStatClick = useCallback(
    (filter: SummaryFilterType) => {
      if (filter === "total") {
        setSummaryFilter(null);
        return;
      }
      setSummaryFilter(summaryFilter === filter ? null : filter);
    },
    [summaryFilter, setSummaryFilter]
  );

  const clearSummaryFilter = useCallback(
    () => setSummaryFilter(null),
    [setSummaryFilter]
  );

  const removeSummaryFilterParam = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("filter");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const removeIncomingStepParam = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("step");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Preferences
  const { data: savedPrefs } = useUserPreference(PREFERENCE_KEY);
  const savePreferencesMutation = useSaveUserPreference();
  const [prefsModalOpen, setPrefsModalOpen] = useState(false);
  const preferences: QueuePreferences =
    (savedPrefs as QueuePreferences | null) ?? DEFAULT_QUEUE_PREFERENCES;

  // Incoming preferences
  const { data: savedIncomingPrefs } = useUserPreference(
    INCOMING_PREFERENCE_KEY
  );
  const incomingPreferences: IncomingQueuePreferences =
    (savedIncomingPrefs as IncomingQueuePreferences | null) ??
    DEFAULT_INCOMING_PREFERENCES;
  const [incomingPrefsModalOpen, setIncomingPrefsModalOpen] = useState(false);

  const incomingSorts = useMemo(
    () => incomingView.sorts ?? (incomingView.sort ? [incomingView.sort] : []),
    [incomingView.sort, incomingView.sorts]
  );
  const incomingRequestFilters = useMemo(
    () => ({
      pathologistId: isPathologist ? pathologistIdForFilter : undefined,
      prefixKeys: incomingFilters.prefixKeys,
      stage: incomingFilters.currentStep,
      currentStatusIds: incomingFilters.currentStatusIds,
      facilityIds: incomingFilters.facilityIds,
      dateFrom: incomingFilters.dateFrom,
      dateTo: incomingFilters.dateTo,
      search: incomingFilters.search,
      isRush: incomingFilters.isRush,
      isHighPriority: incomingFilters.isHighPriority,
      sorts: incomingSorts,
    }),
    [incomingFilters, incomingSorts, isPathologist, pathologistIdForFilter]
  );
  const incomingQuery = useInfinitePathologistIncomingQueue(
    incomingRequestFilters,
    {
      enabled:
        activeTab === "incoming" &&
        queueViewHydrated &&
        !invalidTabs.has("incoming"),
    }
  );
  const incomingCases = useMemo(
    () => incomingQuery.data?.pages.flatMap((result) => result.items) ?? [],
    [incomingQuery.data]
  );
  const incomingData = useMemo(
    () => ({
      cases: incomingCases,
      counts: incomingQuery.data?.pages[0]?.counts ?? {
        grossing: 0,
        embedding: 0,
        cutting: 0,
        staining: 0,
        awaitingSendOut: 0,
        pendingStains: 0,
        total: 0,
      },
    }),
    [incomingCases, incomingQuery.data]
  );
  const incomingTotal = incomingQuery.data?.pages[0]?.total ?? 0;
  const incomingLoading = incomingQuery.isLoading;
  const incomingFetching = incomingQuery.isFetching;
  const refetchIncoming = incomingQuery.refetch;

  const handleIncomingSort = useCallback(
    (columnId: string) => {
      updateQueueViewTab("incoming", (current) => ({
        ...current,
        sort: null,
        sorts: advanceReviewSorts(
          current.sorts ?? (current.sort ? [current.sort] : []),
          columnId
        ),
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  // Sorting
  const reviewSorts = useMemo(() => reviewView.sorts ?? [], [reviewView.sorts]);

  const reviewRequestFilters = useMemo(
    () => ({
      ...backendFilters,
      caseType: filters.caseType,
      tatMinDays: filters.tatMinDays,
      tatOverdue: filters.tatOverdue,
      hasPendingTests: filters.hasPendingTests,
      overdueThresholdDays: preferences.overdueThresholdDays,
      summaryFilter: summaryFilter ?? undefined,
      sorts: reviewSorts,
    }),
    [
      backendFilters,
      filters,
      preferences.overdueThresholdDays,
      reviewSorts,
      summaryFilter,
    ]
  );
  const reviewEnabled =
    activeTab === "review" && queueViewHydrated && !invalidTabs.has("review");
  const activeReviewQuery = useInfinitePathologistReviewQueue(
    { ...reviewRequestFilters, isBlocked: false },
    { enabled: reviewEnabled && filters.isBlocked !== true }
  );
  const blockedReviewQuery = useInfinitePathologistReviewQueue(
    { ...reviewRequestFilters, isBlocked: true },
    { enabled: reviewEnabled && filters.isBlocked !== false }
  );
  const queueCases = useMemo(() => {
    if (!activeReviewQuery.data && !blockedReviewQuery.data) return undefined;
    return [
      ...(activeReviewQuery.data?.pages.flatMap((result) => result.items) ??
        []),
      ...(blockedReviewQuery.data?.pages.flatMap((result) => result.items) ??
        []),
    ];
  }, [activeReviewQuery.data, blockedReviewQuery.data]);
  const activeReviewTotal = activeReviewQuery.data?.pages[0]?.total ?? 0;
  const blockedReviewTotal = blockedReviewQuery.data?.pages[0]?.total ?? 0;
  const isLoading = activeReviewQuery.isLoading || blockedReviewQuery.isLoading;
  const isFetching =
    activeReviewQuery.isFetching || blockedReviewQuery.isFetching;
  const queueError = activeReviewQuery.isError || blockedReviewQuery.isError;
  const refetch = useCallback(async () => {
    await Promise.all([
      activeReviewQuery.refetch(),
      blockedReviewQuery.refetch(),
    ]);
  }, [activeReviewQuery, blockedReviewQuery]);
  const queueStats = useMemo(
    () =>
      stats
        ? {
            ...stats,
            total: queueCases
              ? activeReviewTotal + blockedReviewTotal
              : stats.total,
          }
        : undefined,
    [activeReviewTotal, blockedReviewTotal, queueCases, stats]
  );
  const sentOutCountQuery = useInfinitePathologistReviewQueue(
    { queueScope: "assigned", partition: "waiting" },
    { enabled: queueViewHydrated }
  );
  const sentOutCount = sentOutCountQuery.data?.pages[0]?.total ?? 0;

  // Completed reports retain their existing newest-sign-off default instead
  // of inheriting the active work queue's case-number default.
  const completedSorts = useMemo(
    () =>
      completedView.sorts ?? (completedView.sort ? [completedView.sort] : []),
    [completedView.sort, completedView.sorts]
  );

  const handleSort = useCallback(
    (columnId: string) => {
      updateQueueViewTab("review", (current) => {
        return {
          ...current,
          sorts: advanceReviewSorts(current.sorts ?? [], columnId),
          page: 1,
        };
      });
    },
    [updateQueueViewTab]
  );

  const handleCompletedSort = useCallback(
    (columnId: string) => {
      updateQueueViewTab("completed", (current) => ({
        ...current,
        sort: null,
        sorts: advanceReviewSorts(
          current.sorts ?? (current.sort ? [current.sort] : []),
          columnId
        ),
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  // Visible columns derived from preferences
  const visibleColumns = useMemo(() => {
    return preferences.columns
      .map((id) => ALL_QUEUE_COLUMNS.find((c) => c.id === id))
      .filter(Boolean) as QueueColumnDef[];
  }, [preferences]);

  const reviewColumns = useMemo(
    () => [
      ...buildAssignedReviewColumns(visibleColumns),
      SEND_OUT_STATUS_COLUMN,
    ],
    [visibleColumns]
  );

  const allReviewColumns = useMemo(
    () => buildAllReviewColumns(reviewColumns),
    [reviewColumns]
  );
  const sentOutColumns = useMemo(
    () => [...visibleColumns, SEND_OUT_STATUS_COLUMN],
    [visibleColumns]
  );

  // Completed-tab specific columns: hide active-only caseType/pendingTests
  const completedVisibleColumns = useMemo(() => {
    let cols = preferences.columns
      .map((id) => ALL_QUEUE_COLUMNS.find((c) => c.id === id))
      .filter(Boolean) as QueueColumnDef[];

    // Hide active-only columns
    cols = cols.filter(
      (col) => col.id !== "caseType" && col.id !== "pendingTests"
    );

    // Linear C-4291: Remove redundant 'step' column and capture its index for 'signedOffAt' insertion
    const stepIdx = cols.findIndex((c) => c.id === "step");
    cols = cols.filter((col) => col.id !== "step");

    // Ensure signedOffAt is present for completed tab
    if (!cols.find((c) => c.id === "signedOffAt")) {
      const signedOffCol = ALL_QUEUE_COLUMNS.find(
        (c) => c.id === "signedOffAt"
      );
      if (signedOffCol) {
        // If 'step' was visible, insert 'signedOffAt' at the same index
        if (stepIdx !== -1) {
          cols.splice(stepIdx, 0, signedOffCol);
        } else {
          cols.push(signedOffCol);
        }
      }
    }

    // Override labels for Completed tab context and shallow-copy to avoid mutating global config
    return cols.map((col) => {
      if (col.id === "signedOffAt") {
        return { ...col, label: "Signed Off Date" };
      }
      if (col.id === "qaReview") {
        return { ...col, label: "Pending QA" };
      }
      return { ...col };
    });
  }, [preferences]);

  // Visible incoming columns derived from preferences
  const visibleIncomingColumns = useMemo(() => {
    return incomingPreferences.columns
      .map((id) => ALL_INCOMING_COLUMNS.find((c) => c.id === id))
      .filter(Boolean) as QueueColumnDef[];
  }, [incomingPreferences]);
  const reviewTableWidth = useMemo(
    () =>
      reviewColumns.reduce((width, column) => width + (column.width ?? 120), 0),
    [reviewColumns]
  );
  const incomingTableWidth = useMemo(
    () =>
      visibleIncomingColumns.reduce(
        (width, column) => width + (column.width ?? 120),
        0
      ),
    [visibleIncomingColumns]
  );
  const completedTableWidth = useMemo(
    () =>
      completedVisibleColumns.reduce(
        (width, column) => width + (column.width ?? 120),
        0
      ),
    [completedVisibleColumns]
  );

  const incomingPrefixOptions = useMemo(
    () => incomingQuery.data?.pages[0]?.prefixOptions ?? [],
    [incomingQuery.data]
  );
  const savedIncomingDefault = useMemo(
    () =>
      decodePathologistQueueDefault(
        rawDefaults.incoming,
        incomingPrefixOptions,
        "incoming"
      ),
    [rawDefaults.incoming, incomingPrefixOptions]
  );

  // The backend filters before taking each incoming page.
  const filteredIncomingCases = incomingCases;

  const hasActiveIncomingFilters = Object.entries(incomingFilters).some(
    ([, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "string") return value.length > 0;
      if (typeof value === "boolean") return true;
      if (typeof value === "number") return true;
      return !!value;
    }
  );

  // The server applies the ordered sort across the full matching queue.
  const sortedIncomingCases = filteredIncomingCases;
  const revealedIncomingCases = sortedIncomingCases;
  const hasMoreIncomingCases = Boolean(incomingQuery.hasNextPage);

  // ===== MY COMPLETED CASES TAB (the pathologist's own signed-off cases) =====
  const completedFilters = completedView.filters;
  const [
    completedFilterModalOpened,
    { open: openCompletedFilterModal, close: closeCompletedFilterModal },
  ] = useDisclosure(false);

  const completedRequestFilters = useMemo(
    () => ({
      prefixKeys: completedFilters.prefixKeys,
      search: completedFilters.search,
      dateFrom: completedFilters.dateFrom,
      dateTo: completedFilters.dateTo,
      isRush: completedFilters.isRush,
      isHighPriority: completedFilters.isHighPriority,
      caseType: completedFilters.caseType,
      facilityIds: completedFilters.facilityIds,
      sorts: completedSorts,
    }),
    [completedFilters, completedSorts]
  );
  const completedQuery = useInfinitePathologistCompletedCases(
    completedRequestFilters,
    {
      enabled:
        isPathologist &&
        activeTab === "completed" &&
        queueViewHydrated &&
        !invalidTabs.has("completed"),
    }
  );
  const completedCases = useMemo(
    () => completedQuery.data?.pages.flatMap((result) => result.items) ?? [],
    [completedQuery.data]
  );
  const completedPrefixOptions = useMemo(
    () => completedQuery.data?.pages[0]?.prefixOptions ?? [],
    [completedQuery.data]
  );
  const completedTotal = completedQuery.data?.pages[0]?.total ?? 0;
  const completedLoading = completedQuery.isLoading;
  const completedFetching = completedQuery.isFetching;
  const refetchCompleted = completedQuery.refetch;
  const savedCompletedDefault = useMemo(
    () =>
      decodePathologistQueueDefault(
        rawDefaults.completed,
        completedPrefixOptions,
        "completed"
      ),
    [rawDefaults.completed, completedPrefixOptions]
  );

  // Completed filters and ordered sorts are applied before each database page.
  const sortedCompletedCases = completedCases;
  const revealedCompletedCases = sortedCompletedCases;
  const hasMoreCompletedCases = Boolean(completedQuery.hasNextPage);

  const hasActiveCompletedFilters = useMemo(
    () =>
      Object.entries(completedFilters).some(([, value]) => {
        if (value === undefined || value === null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.length > 0;
        if (typeof value === "boolean") return true;
        if (typeof value === "number") return true;
        return !!value;
      }),
    [completedFilters]
  );

  const handleCompletedFiltersApply = useCallback(
    (newFilters: CaseFilters) => {
      updateQueueViewTab("completed", (current) => ({
        ...current,
        filters: newFilters,
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleClearCompletedFilters = useCallback(() => {
    clearQueueViewTab("completed");
  }, [clearQueueViewTab]);

  const handleRefreshCompleted = useCallback(() => {
    refetchCompleted();
  }, [refetchCompleted]);

  const handleCompletedSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      updateQueueViewTab("completed", (current) => ({
        ...current,
        filters: { ...current.filters, search: value },
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  // The server applies review filters and ordered sorts before it takes a page.
  const { activeCases, blockedCases } = useMemo(() => {
    const loaded = queueCases ?? [];
    return {
      activeCases: loaded.filter((caseItem) => !caseItem.isBlocked),
      blockedCases: loaded.filter((caseItem) => caseItem.isBlocked),
    };
  }, [queueCases]);
  const sortedActiveCases = activeCases;
  const sortedBlockedCases = blockedCases;
  const revealedActiveCases = sortedActiveCases;
  const hasMoreActiveCases = Boolean(activeReviewQuery.hasNextPage);
  const hasMoreBlockedCases = Boolean(blockedReviewQuery.hasNextPage);
  const paginatedBlockedCases = sortedBlockedCases;
  const hasActiveInlineFilters = Boolean(
    summaryFilter ||
      filters.caseType ||
      filters.tatOverdue ||
      filters.tatMinDays != null ||
      filters.hasPendingTests != null
  );

  const hasActiveFilters = Object.values(filters).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    if (typeof value === "boolean") return true;
    if (typeof value === "number") return true;
    return !!value;
  });

  const handleFiltersApply = useCallback(
    (newFilters: CaseFilters) => {
      updateQueueViewTab("review", (current) => ({
        ...current,
        filters: newFilters,
        page: 1,
        blockedPage: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handlePrefixKeysChange = useCallback(
    (selectedKeys: string[]) => {
      updateQueueViewTab("review", (current) => {
        const unavailableKeys = (current.filters.prefixKeys ?? []).filter(
          (key) => !prefixOptions.includes(key)
        );
        const prefixKeys = Array.from(
          new Set([...unavailableKeys, ...selectedKeys])
        );
        return {
          ...current,
          filters: {
            ...current.filters,
            prefixKeys: prefixKeys.length ? prefixKeys : undefined,
          },
          page: 1,
          blockedPage: 1,
        };
      });
    },
    [prefixOptions, updateQueueViewTab]
  );

  const handleClearAllFilters = useCallback(() => {
    clearQueueViewTab("review");
    removeSummaryFilterParam();
  }, [clearQueueViewTab, removeSummaryFilterParam]);

  const handleSaveDefault = useCallback(async () => {
    setDefaultMessage(null);
    setDefaultError(null);
    const snapshot = createPathologistQueueDefault(
      { ...reviewView, summaryFilter },
      eligiblePrefixKeys
    );
    if (!snapshot) {
      setDefaultError(
        "This view cannot be saved until its prefix choices are available and valid."
      );
      return;
    }
    setDefaultSaving(true);
    try {
      await saveDefault("review", snapshot);
      setDefaultMessage("Default saved. Your current view has not changed.");
    } catch {
      setDefaultError("The default could not be saved. Please try again.");
    } finally {
      setDefaultSaving(false);
    }
  }, [eligiblePrefixKeys, reviewView, saveDefault, summaryFilter]);

  const handleApplyDefault = useCallback(() => {
    setDefaultMessage(null);
    setDefaultError(null);
    if (!savedDefault) {
      setDefaultError(
        "The saved default is unavailable or no longer valid for this queue."
      );
      return;
    }
    updateQueueViewTab("review", (current) =>
      applyPathologistQueueDefault(current, savedDefault)
    );
    removeSummaryFilterParam();
    setDefaultMessage("Default applied to your current view.");
  }, [removeSummaryFilterParam, savedDefault, updateQueueViewTab]);

  const handleReviewFiltersChange = useCallback(
    (nextFilters: CaseFilters) => {
      updateQueueViewTab("review", (current) => ({
        ...current,
        filters: nextFilters,
        page: 1,
        blockedPage: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleRevealMoreReviewCases = useCallback(() => {
    if (!hasMoreActiveCases || activeReviewQuery.isFetchingNextPage) return;
    void activeReviewQuery.fetchNextPage();
  }, [activeReviewQuery, hasMoreActiveCases]);

  const attachReviewLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      reviewBoundaryObserver.current?.disconnect();
      if (
        !node ||
        activeTab !== "review" ||
        !hasMoreActiveCases ||
        activeReviewQuery.isFetchingNextPage ||
        isLoading
      )
        return;
      if (typeof IntersectionObserver === "undefined") return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreReviewCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      reviewBoundaryObserver.current = observer;
      observer.observe(node);
    },
    [
      activeReviewQuery.isFetchingNextPage,
      activeTab,
      handleRevealMoreReviewCases,
      hasMoreActiveCases,
      isLoading,
    ]
  );

  const handleRevealMoreBlockedCases = useCallback(() => {
    if (!hasMoreBlockedCases || blockedReviewQuery.isFetchingNextPage) return;
    void blockedReviewQuery.fetchNextPage();
  }, [blockedReviewQuery, hasMoreBlockedCases]);

  const attachBlockedLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      blockedBoundaryObserver.current?.disconnect();
      if (
        !node ||
        activeTab !== "review" ||
        !hasMoreBlockedCases ||
        blockedReviewQuery.isFetchingNextPage ||
        typeof IntersectionObserver === "undefined"
      )
        return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreBlockedCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      blockedBoundaryObserver.current = observer;
      observer.observe(node);
    },
    [
      activeTab,
      blockedReviewQuery.isFetchingNextPage,
      handleRevealMoreBlockedCases,
      hasMoreBlockedCases,
    ]
  );

  const handleRetryReviewQueue = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleBarcodeSubmit = useCallback(async () => {
    if (!barcodeInput.trim()) {
      return;
    }

    setError(null);

    try {
      const caseData = await openCaseMutation.mutateAsync({
        barcode: barcodeInput.trim(),
        intent: "pathologist_review",
      });
      const reviewStatuses = [
        "Pending Pathologist Review",
        "Pathologist Review",
        "Signed Off",
      ];
      if (reviewStatuses.includes(caseData.currentStatus?.name)) {
        navigate(`/pathologist-review/${caseData.id}`, {
          state: queueReturnState,
        });
      } else {
        navigate(`/cases/${caseData.id}`, { state: queueReturnState });
      }
    } catch (err: unknown) {
      // Map to plain-language user-facing string at the gateway/service boundary
      // For this specific local catch, we use a friendly default if translation missing.
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Case not found. Check the barcode and try again.";
      setError(errorMessage);
      setBarcodeInput("");
    }
  }, [barcodeInput, openCaseMutation, navigate, queueReturnState]);

  const handleBarcodeKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleBarcodeSubmit();
      }
    },
    [handleBarcodeSubmit]
  );

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        setBarcodeInput(barcode);
        setError(null);
        try {
          const caseData = await openCaseMutation.mutateAsync({
            barcode,
            intent: "pathologist_review",
          });
          const reviewStatuses = [
            "Pending Pathologist Review",
            "Pathologist Review",
            "Signed Off",
          ];
          if (reviewStatuses.includes(caseData.currentStatus?.name)) {
            navigate(`/pathologist-review/${caseData.id}`, {
              state: queueReturnState,
            });
          } else {
            navigate(`/cases/${caseData.id}`, { state: queueReturnState });
          }
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Case not found. Check the barcode and try again.";
          setError(errorMessage);
          setBarcodeInput("");
        }
      },
      [openCaseMutation, navigate, queueReturnState]
    )
  );

  const handleRowClick = useCallback(
    (caseItem: PathologistReviewQueueCase, lockedByOther: boolean) => {
      if (caseItem.reviewAction === "tc_completion") {
        navigate(`/cases/${caseItem.id}`, { state: queueReturnState });
        return;
      }
      if (lockedByOther) {
        navigate(`/cases/${caseItem.id}`, { state: queueReturnState });
      } else {
        navigate(`/pathologist-review/${caseItem.id}`, {
          state: queueReturnState,
        });
      }
    },
    [navigate, queueReturnState]
  );

  const formatTatFromDate = useCallback(
    (dateReceived: string): string => {
      const diff = getQueueTurnaroundDays(dateReceived, labToday);
      if (diff === 0) return "Today";
      if (diff === 1) return "1d";
      if (diff < 7) return `${diff}d`;
      if (diff < 14) return "1w";
      if (diff < 30) return `${Math.floor(diff / 7)}w`;
      if (diff < 60) return "1mo";
      return `${Math.floor(diff / 30)}mo`;
    },
    [labToday]
  );

  const formatTat = useCallback(
    (caseItem: PathologistReviewQueueCase): string =>
      formatTatFromDate(caseItem.dateReceived),
    [formatTatFromDate]
  );

  const renderCell = useCallback(
    (
      col: QueueColumnDef,
      caseItem: PathologistReviewQueueCase,
      isLockedByOther: boolean
    ) => {
      const physician = caseItem.physician;
      const isOverdue =
        getQueueTurnaroundDays(caseItem.dateReceived, labToday) >
        preferences.overdueThresholdDays;
      switch (col.id) {
        case "flag": {
          const rowAppearance = getPathologistReviewRowAppearance(caseItem);
          return (
            <Group gap={4} wrap="nowrap">
              {rowAppearance && (
                <PathologistQueueStateBadge appearance={rowAppearance} />
              )}
              {caseItem.isHighPriority && !caseItem.isRush && (
                <Badge color="orange" variant="filled" size="xs">
                  HIGH
                </Badge>
              )}
              {caseItem.hasOpenDiscussion && (
                <IconMessageCircle
                  size={14}
                  color="var(--mantine-color-grape-6)"
                />
              )}

              {isOverdue && <IconFlag size={14} color="red" />}
              {caseItem.lockedBy && (
                <Tooltip
                  label={`Locked by ${caseItem.lockedByName ?? "another user"}`}
                  withArrow
                >
                  <IconLock
                    size={14}
                    color={isLockedByOther ? "orange" : "green"}
                  />
                </Tooltip>
              )}
            </Group>
          );
        }
        case "caseNumber":
          return (
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                {caseItem.caseNumber}
              </Text>
              {caseItem.externalCaseNumber && (
                <Text size="xs" c="dimmed">
                  {caseItem.externalCaseNumber}
                </Text>
              )}
            </Stack>
          );
        case "patient":
          return (
            <Text size="sm">
              {caseItem.patient
                ? `${caseItem.patient.lastName}, ${caseItem.patient.firstName}`
                : "—"}
            </Text>
          );
        case "dob": {
          const dob = caseItem.patient?.dateOfBirth;
          if (!dob) return <Text size="sm">—</Text>;
          const age = getAgeOnDate(dob, labToday);
          return (
            <Text size="sm">
              {formatQueueDate(dob)}{" "}
              <Text span c="dimmed" size="xs">
                {age === null ? "" : `(${age}y)`}
              </Text>
            </Text>
          );
        }
        case "physician":
          return (
            <Text size="sm">
              {physician
                ? `${physician.lastName}, ${physician.firstName}`
                : "—"}
            </Text>
          );
        case "assignedPathologist":
          return (
            <Text size="sm">
              {caseItem.pathologist?.fullName || "Unassigned"}
            </Text>
          );
        case "caseType": {
          const isSignedOff = caseItem.currentStatus?.name === "Signed Off";
          const hasPendingStains =
            (caseItem.pendingAdditionalStainsCount ?? 0) > 0;
          const rawType =
            caseItem.caseType ??
            (isSignedOff && hasPendingStains
              ? "Awaiting Stains"
              : "Initial Review");
          // Send-out cases display as "Amendment" in the Review Type column
          const typeLabel =
            rawType === "External Opinion" || rawType === "External Stain"
              ? "Amendment"
              : rawType;
          const typeColor =
            typeLabel === "Malignancy"
              ? "red"
              : typeLabel === "Addendum"
              ? "blue"
              : typeLabel === "Amendment" || typeLabel === "Correction"
              ? "orange"
              : typeLabel === "Awaiting Stains"
              ? "yellow"
              : "gray";
          return (
            <Badge size="xs" color={typeColor} variant="light">
              {typeLabel}
            </Badge>
          );
        }
        case "tat":
          return (
            <Group gap={4} wrap="nowrap">
              <Text size="sm">{formatTat(caseItem)}</Text>
              {isOverdue && <IconFlag size={14} color="red" />}
            </Group>
          );
        case "pendingTests": {
          const stainCount = caseItem.pendingAdditionalStainsCount ?? 0;
          return stainCount > 0 ? (
            <Text size="sm" fw={500}>
              Yes ({stainCount})
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              No
            </Text>
          );
        }
        case "step": {
          const statusName = caseItem.currentStatus?.name;
          let stepLabel = "—";
          let stepColor: string | undefined;
          if (caseItem.caseType === "External Opinion") {
            stepLabel = "External Opinion";
            stepColor = "violet";
          } else if (caseItem.caseType === "External Stain") {
            stepLabel = "External Stain";
            stepColor = "cyan";
          } else if (statusName === "Pending Pathologist Review") {
            stepLabel = "Pending Review";
          } else if (statusName === "Pathologist Review") {
            stepLabel = "In Review";
          } else if (statusName === "Pending External Report") {
            stepLabel = "Awaiting External Report";
            stepColor = "violet";
          } else if (statusName === "Completed") {
            stepLabel = "Completed";
            stepColor = "dimmed";
          } else if (statusName === "Signed Off") {
            stepLabel = "Signed Off";
            stepColor = "dimmed";
          } else if (statusName) {
            stepLabel = statusName;
          }
          return (
            <Text
              size="sm"
              c={stepColor}
              fw={
                stepColor === "orange" ||
                stepColor === "violet" ||
                stepColor === "cyan"
                  ? 500
                  : undefined
              }
            >
              {stepLabel}
            </Text>
          );
        }
        case "qaReview":
          return caseItem.hasPendingQaReview ? (
            <Text size="sm" fw={500} c="green">
              Yes
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              No
            </Text>
          );
        case "specimens": {
          const totalSlides =
            (caseItem.slideCount || 0) +
            (caseItem.additionalStainSlideCount || 0);
          return (
            <Text size="sm">
              {caseItem.specimenCount || 0} spec / {totalSlides} slides
            </Text>
          );
        }
        case "facility":
          return <Text size="sm">{caseItem.facility?.name || "—"}</Text>;
        case "workflow": {
          const workflowLabels: Record<string, string> = {
            global: "Global",
            pass_through: "Pass Through",
            consultation: "Consultation",
            tc_only: "TC Only",
          };
          const wfType = caseItem.workflow?.accessioningType;
          return (
            <Text size="sm">
              {wfType ? workflowLabels[wfType] ?? wfType : "—"}
            </Text>
          );
        }
        case "procedure":
          return <Text size="sm">{caseItem.procedure?.name || "—"}</Text>;
        case "practice":
          return <Text size="sm">{caseItem.sendingPractice?.name || "—"}</Text>;
        case "mrn":
          return (
            <Text size="sm">
              {caseItem.patient?.medicalRecordNumber || "—"}
            </Text>
          );
        case "dateCollected":
          return (
            <Text size="sm">
              {caseItem.dateCollected
                ? formatQueueDate(caseItem.dateCollected)
                : "—"}
            </Text>
          );
        case "signedOffAt":
          return (
            <Text size="sm">
              {caseItem.signedOffAt
                ? formatQueueInstant(caseItem.signedOffAt)
                : "—"}
            </Text>
          );
        case "sendOutStatus":
          if (caseItem.sendOutReviewStatus === "pending_shipping") {
            return <Text size="sm">Pending shipping</Text>;
          }
          if (caseItem.sendOutReviewStatus === "sent_out") {
            return (
              <Text size="sm">
                Sent out{" "}
                {caseItem.sendOutShippedAt
                  ? formatQueueInstant(caseItem.sendOutShippedAt)
                  : ""}
              </Text>
            );
          }
          if (caseItem.sendOutReviewStatus === "report_ready") {
            return <Text size="sm">Report/addendum ready</Text>;
          }
          return <Text size="sm">—</Text>;
        case "priority":
          return caseItem.isRush || caseItem.isHighPriority ? (
            <Group gap={4} wrap="nowrap">
              {caseItem.isRush && (
                <Badge color="red" variant="filled" size="xs">
                  STAT
                </Badge>
              )}
              {caseItem.isHighPriority && (
                <Badge color="orange" variant="filled" size="xs">
                  HIGH
                </Badge>
              )}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Normal
            </Text>
          );
        case "tcReviewAction":
          return caseItem.reviewAction === "tc_completion" ? (
            <TcReviewCompletionButton
              caseId={caseItem.id}
              assignmentRevision={caseItem.pathologistAssignmentRevision ?? 0}
              canComplete={
                caseItem.pathologist?.id === currentUserId ||
                canEditAnyPathologistReview
              }
            />
          ) : null;
        default:
          return null;
      }
    },
    [
      preferences.overdueThresholdDays,
      formatTat,
      labToday,
      canEditAnyPathologistReview,
      currentUserId,
    ]
  );

  const renderTableHeader = useCallback(
    (
      columns: QueueColumnDef[],
      currentSortState: QueueSortState | QueueSortState[] | null = reviewSorts,
      onSort = handleSort
    ) => {
      const orderedReviewSorts = Array.isArray(currentSortState);
      const activeSorts = orderedReviewSorts
        ? currentSortState
        : currentSortState
        ? [currentSortState]
        : [];
      return (
        <Table.Thead>
          <Table.Tr>
            {columns.map((col) => {
              const priority = activeSorts.findIndex(
                (sort) => sort.column === col.id
              );
              const activeSort = activeSorts[priority];
              return (
                <Table.Th
                  key={col.id}
                  aria-sort={
                    priority === 0
                      ? activeSort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  style={{
                    width: col.width,
                  }}
                >
                  <Group gap={4} wrap="nowrap">
                    <button
                      type="button"
                      onClick={() => col.sortable && onSort(col.id)}
                      aria-label={
                        col.sortable
                          ? orderedReviewSorts && activeSort
                            ? `${col.label}, ${
                                activeSort.direction === "asc"
                                  ? "ascending"
                                  : "descending"
                              }, priority ${priority + 1}`
                            : orderedReviewSorts
                            ? `${col.label}, unsorted`
                            : col.label
                          : undefined
                      }
                      style={{
                        cursor: col.sortable ? "pointer" : "default",
                        background: "none",
                        border: "none",
                        padding: 0,
                        font: "inherit",
                        color: "inherit",
                      }}
                    >
                      {col.label}
                    </button>
                    {col.tooltip && <ColumnTooltip text={col.tooltip} />}
                    {col.sortable &&
                      activeSort &&
                      (activeSort.direction === "asc" ? (
                        <IconSortAscending size={14} />
                      ) : (
                        <IconSortDescending size={14} />
                      ))}
                    {col.sortable && orderedReviewSorts && activeSort && (
                      <Text size="xs" fw={700} aria-hidden="true">
                        {priority + 1}
                      </Text>
                    )}
                  </Group>
                </Table.Th>
              );
            })}
          </Table.Tr>
        </Table.Thead>
      );
    },
    [reviewSorts, handleSort]
  );

  const renderCaseRow = useCallback(
    (
      caseItem: PathologistReviewQueueCase,
      isBlockedTable = false,
      columns: QueueColumnDef[]
    ) => {
      const isLockedByOther =
        caseItem.lockedBy && caseItem.lockedBy !== currentUserId;
      const rowAppearance = getPathologistReviewRowAppearance(caseItem);
      const hasVisibleFlagColumn = columns.some(
        (column) => column.id === "flag"
      );

      return (
        <Table.Tr
          key={caseItem.id}
          title={rowAppearance?.label}
          data-color-code={rowAppearance?.key}
          style={{
            cursor: "pointer",
            opacity: isLockedByOther ? 0.6 : 1,
            backgroundColor: rowAppearance?.background,
          }}
          onClick={() => {
            if (isBlockedTable) {
              navigate(`/cases/${caseItem.id}?tab=errors`, {
                state: queueReturnState,
              });
            } else if (activeTab === "sentOut") {
              navigate(`/cases/${caseItem.id}`, { state: queueReturnState });
            } else {
              handleRowClick(caseItem, !!isLockedByOther);
            }
          }}
        >
          {columns.map((col, columnIndex) => (
            <Table.Td key={col.id}>
              {columnIndex === 0 && rowAppearance && (
                <VisuallyHidden>
                  Row state: {rowAppearance.label}
                </VisuallyHidden>
              )}
              {columnIndex === 0 && rowAppearance && !hasVisibleFlagColumn ? (
                <Group gap={4} wrap="nowrap">
                  <PathologistQueueStateBadge appearance={rowAppearance} />
                  <Box>{renderCell(col, caseItem, !!isLockedByOther)}</Box>
                </Group>
              ) : (
                renderCell(col, caseItem, !!isLockedByOther)
              )}
            </Table.Td>
          ))}
        </Table.Tr>
      );
    },
    [
      currentUserId,
      navigate,
      handleRowClick,
      renderCell,
      queueReturnState,
      activeTab,
    ]
  );

  const renderIncomingCell = useCallback(
    (col: QueueColumnDef, caseItem: IncomingLabCase) => {
      switch (col.id) {
        case "flag":
          return (
            <Group gap={4} wrap="nowrap">
              {caseItem.isRush && (
                <Badge color="red" variant="filled" size="xs">
                  STAT
                </Badge>
              )}
              {caseItem.isHighPriority && !caseItem.isRush && (
                <Badge color="orange" variant="filled" size="xs">
                  HIGH
                </Badge>
              )}
            </Group>
          );
        case "caseNumber":
          return (
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                {caseItem.caseNumber}
              </Text>
              {caseItem.externalCaseNumber && (
                <Text size="xs" c="dimmed">
                  {caseItem.externalCaseNumber}
                </Text>
              )}
            </Stack>
          );
        case "patient":
          return (
            <Text size="sm">
              {caseItem.patient
                ? `${caseItem.patient.lastName}, ${caseItem.patient.firstName}`
                : "—"}
            </Text>
          );
        case "dob": {
          const dob = caseItem.patient?.dateOfBirth;
          const age = dob ? getAgeOnDate(dob, labToday) : null;
          return (
            <Text size="sm">
              {dob && age !== null ? `${formatQueueDate(dob)} (${age}y)` : "—"}
            </Text>
          );
        }
        case "physician":
          return <Text size="sm">{caseItem.physician?.fullName || "—"}</Text>;
        case "currentStep": {
          const step = caseItem.currentStep;
          return (
            <Badge
              size="sm"
              variant="light"
              color={INCOMING_STEP_COLORS[step] ?? "gray"}
            >
              {step}
            </Badge>
          );
        }
        case "tat":
          return (
            <Text size="sm">{formatTatFromDate(caseItem.dateReceived)}</Text>
          );
        case "dateReceived":
          return (
            <Text size="sm">
              {caseItem.dateReceived
                ? formatQueueDate(caseItem.dateReceived)
                : "—"}
            </Text>
          );
        case "dateCollected":
          return (
            <Text size="sm">
              {caseItem.dateCollected
                ? formatQueueDate(caseItem.dateCollected)
                : "—"}
            </Text>
          );
        case "specimens":
          return (
            <Text size="sm">
              {caseItem.specimenCount ?? 0} spec / {caseItem.blockCount ?? 0}{" "}
              blocks
            </Text>
          );
        case "procedure":
          return <Text size="sm">{caseItem.procedureName || "—"}</Text>;
        case "practice":
          return <Text size="sm">{caseItem.sendingPractice?.name || "—"}</Text>;
        case "workflow":
          return (
            <Text size="sm">
              {WORKFLOW_LABELS[caseItem.accessioningType ?? ""] ??
                caseItem.accessioningType ??
                "—"}
            </Text>
          );
        case "mrn":
          return <Text size="sm">{caseItem.mrn || "—"}</Text>;
        case "priority":
          return caseItem.isRush || caseItem.isHighPriority ? (
            <Group gap={4} wrap="nowrap">
              {caseItem.isRush && (
                <Badge color="red" variant="filled" size="xs">
                  STAT
                </Badge>
              )}
              {caseItem.isHighPriority && (
                <Badge color="orange" variant="filled" size="xs">
                  HIGH
                </Badge>
              )}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Normal
            </Text>
          );
        case "facility":
          return <Text size="sm">{caseItem.facility?.name || "—"}</Text>;
        default:
          return null;
      }
    },
    [formatTatFromDate, labToday]
  );

  const renderIncomingTableHeader = useCallback(
    () => (
      <Table.Thead>
        <Table.Tr>
          {visibleIncomingColumns.map((col) => {
            const priority = incomingSorts.findIndex(
              (sort) => sort.column === col.id
            );
            const activeSort = incomingSorts[priority];
            return (
              <Table.Th
                key={col.id}
                aria-sort={
                  priority === 0
                    ? activeSort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : undefined
                }
                style={{ width: col.width }}
              >
                <Group gap={4} wrap="nowrap">
                  <button
                    type="button"
                    onClick={() => col.sortable && handleIncomingSort(col.id)}
                    aria-label={
                      col.sortable
                        ? activeSort
                          ? `${col.label}, ${
                              activeSort.direction === "asc"
                                ? "ascending"
                                : "descending"
                            }, priority ${priority + 1}`
                          : `${col.label}, unsorted`
                        : undefined
                    }
                    style={{
                      cursor: col.sortable ? "pointer" : "default",
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                      color: "inherit",
                    }}
                  >
                    {col.label}
                  </button>
                  {col.tooltip && <ColumnTooltip text={col.tooltip} />}
                  {col.sortable &&
                    activeSort &&
                    (activeSort.direction === "asc" ? (
                      <IconSortAscending size={14} />
                    ) : (
                      <IconSortDescending size={14} />
                    ))}
                  {col.sortable && activeSort && (
                    <Text size="xs" fw={700} aria-hidden="true">
                      {priority + 1}
                    </Text>
                  )}
                </Group>
              </Table.Th>
            );
          })}
        </Table.Tr>
      </Table.Thead>
    ),
    [visibleIncomingColumns, incomingSorts, handleIncomingSort]
  );

  const renderIncomingCaseRow = useCallback(
    (caseItem: IncomingLabCase) => {
      return (
        <Table.Tr
          key={caseItem.id}
          style={{
            cursor: "pointer",
            backgroundColor: caseItem.isRush
              ? "var(--mantine-color-red-0)"
              : undefined,
          }}
          onClick={() =>
            navigate(`/pathologist-review/${caseItem.id}`, {
              state: createPathologistQueueReturnState("incoming"),
            })
          }
        >
          {visibleIncomingColumns.map((col) => (
            <Table.Td key={col.id}>
              {renderIncomingCell(col, caseItem)}
            </Table.Td>
          ))}
        </Table.Tr>
      );
    },
    [visibleIncomingColumns, navigate, renderIncomingCell]
  );

  const handleBarcodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBarcodeInput(e.target.value);
    },
    []
  );

  const handleOpenPrefs = useCallback(() => setPrefsModalOpen(true), []);

  const handleIncomingStepClear = useCallback(
    () => setIncomingStep(undefined),
    [setIncomingStep]
  );

  const handleResetReviewFilters = useCallback(() => {
    clearQueueViewTab("review");
    removeSummaryFilterParam();
  }, [clearQueueViewTab, removeSummaryFilterParam]);

  const handleIncomingStepGrossing = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "grossing" ? undefined : "grossing"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleIncomingStepEmbedding = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "embedding" ? undefined : "embedding"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleIncomingStepCutting = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "cutting" ? undefined : "cutting"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleIncomingStepStaining = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "staining" ? undefined : "staining"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleIncomingStepSendOut = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "awaiting_send_out"
          ? undefined
          : "awaiting_send_out"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleIncomingStepPendingStains = useCallback(
    () =>
      setIncomingStep(
        incomingFilters.currentStep === "pending_stains"
          ? undefined
          : "pending_stains"
      ),
    [incomingFilters.currentStep, setIncomingStep]
  );

  const handleClosePrefs = useCallback(() => setPrefsModalOpen(false), []);

  const handleCloseIncomingPrefs = useCallback(
    () => setIncomingPrefsModalOpen(false),
    []
  );

  const handleOpenIncomingPrefs = useCallback(
    () => setIncomingPrefsModalOpen(true),
    []
  );

  const handleSavePrefs = useCallback(
    (prefs: QueuePreferences) => {
      savePreferencesMutation.mutate(
        { key: PREFERENCE_KEY, value: prefs },
        { onSuccess: handleClosePrefs }
      );
    },
    [savePreferencesMutation, handleClosePrefs]
  );

  const handleSaveIncomingPrefs = useCallback(
    (prefs: IncomingQueuePreferences) => {
      savePreferencesMutation.mutate(
        { key: INCOMING_PREFERENCE_KEY, value: prefs },
        { onSuccess: handleCloseIncomingPrefs }
      );
    },
    [savePreferencesMutation, handleCloseIncomingPrefs]
  );

  const handleRefreshIncoming = useCallback(
    () => refetchIncoming(),
    [refetchIncoming]
  );

  const handleDistributionNavigate = useCallback(
    () => navigate("/distribution/control-slides"),
    [navigate]
  );

  const handleClearIncomingFilters = useCallback(() => {
    clearQueueViewTab("incoming");
    removeIncomingStepParam();
  }, [clearQueueViewTab, removeIncomingStepParam]);

  const handleResetIncomingFilters = useCallback(() => {
    clearQueueViewTab("incoming");
    removeIncomingStepParam();
  }, [clearQueueViewTab, removeIncomingStepParam]);

  const handleIncomingFiltersChange = useCallback(
    (nextFilters: CaseFilters) => {
      updateQueueViewTab("incoming", (current) => ({
        ...current,
        filters: nextFilters,
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleIncomingPrefixChange = useCallback(
    (prefixKeys: string[]) => {
      updateQueueViewTab("incoming", (current) => ({
        ...current,
        filters: {
          ...current.filters,
          prefixKeys: prefixKeys.length ? prefixKeys : undefined,
        },
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleSaveIncomingDefault = useCallback(async () => {
    setIncomingDefaultMessage(null);
    setIncomingDefaultError(null);
    const snapshot = createPathologistQueueDefault(
      incomingView,
      incomingPrefixOptions,
      "incoming"
    );
    if (!snapshot) {
      setIncomingDefaultError(
        "This view cannot be saved until its prefix choices are available and valid."
      );
      return;
    }
    setIncomingDefaultSaving(true);
    try {
      await saveDefault("incoming", snapshot);
      setIncomingDefaultMessage(
        "Default saved. Your current view has not changed."
      );
    } catch {
      setIncomingDefaultError(
        "The default could not be saved. Please try again."
      );
    } finally {
      setIncomingDefaultSaving(false);
    }
  }, [incomingView, incomingPrefixOptions, saveDefault]);

  const handleApplyIncomingDefault = useCallback(() => {
    setIncomingDefaultMessage(null);
    setIncomingDefaultError(null);
    if (!savedIncomingDefault) {
      setIncomingDefaultError(
        "The saved default is unavailable or no longer valid for this queue."
      );
      return;
    }
    updateQueueViewTab("incoming", (current) =>
      applyPathologistQueueDefault(current, savedIncomingDefault, "incoming")
    );
    removeIncomingStepParam();
    setIncomingDefaultMessage("Default applied to your current view.");
  }, [savedIncomingDefault, updateQueueViewTab, removeIncomingStepParam]);

  const handleRevealMoreIncomingCases = useCallback(() => {
    if (!hasMoreIncomingCases || incomingQuery.isFetchingNextPage) return;
    void incomingQuery.fetchNextPage();
  }, [hasMoreIncomingCases, incomingQuery]);

  const attachIncomingLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      incomingBoundaryObserver.current?.disconnect();
      if (
        !node ||
        activeTab !== "incoming" ||
        !hasMoreIncomingCases ||
        incomingQuery.isFetchingNextPage ||
        incomingLoading ||
        typeof IntersectionObserver === "undefined"
      )
        return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreIncomingCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      incomingBoundaryObserver.current = observer;
      observer.observe(node);
    },
    [
      activeTab,
      hasMoreIncomingCases,
      incomingQuery.isFetchingNextPage,
      incomingLoading,
      handleRevealMoreIncomingCases,
    ]
  );

  const handleCompletedFiltersChange = useCallback(
    (nextFilters: CaseFilters) => {
      updateQueueViewTab("completed", (current) => ({
        ...current,
        filters: nextFilters,
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleCompletedPrefixChange = useCallback(
    (prefixKeys: string[]) => {
      updateQueueViewTab("completed", (current) => ({
        ...current,
        filters: {
          ...current.filters,
          prefixKeys: prefixKeys.length ? prefixKeys : undefined,
        },
        page: 1,
      }));
    },
    [updateQueueViewTab]
  );

  const handleSaveCompletedDefault = useCallback(async () => {
    setCompletedDefaultMessage(null);
    setCompletedDefaultError(null);
    const snapshot = createPathologistQueueDefault(
      completedView,
      completedPrefixOptions,
      "completed"
    );
    if (!snapshot) {
      setCompletedDefaultError(
        "This view cannot be saved until its prefix choices are available and valid."
      );
      return;
    }
    setCompletedDefaultSaving(true);
    try {
      await saveDefault("completed", snapshot);
      setCompletedDefaultMessage(
        "Default saved. Your current view has not changed."
      );
    } catch {
      setCompletedDefaultError(
        "The default could not be saved. Please try again."
      );
    } finally {
      setCompletedDefaultSaving(false);
    }
  }, [completedView, completedPrefixOptions, saveDefault]);

  const handleApplyCompletedDefault = useCallback(() => {
    setCompletedDefaultMessage(null);
    setCompletedDefaultError(null);
    if (!savedCompletedDefault) {
      setCompletedDefaultError(
        "The saved default is unavailable or no longer valid for this queue."
      );
      return;
    }
    updateQueueViewTab("completed", (current) =>
      applyPathologistQueueDefault(current, savedCompletedDefault, "completed")
    );
    setCompletedDefaultMessage("Default applied to your current view.");
  }, [savedCompletedDefault, updateQueueViewTab]);

  const handleRevealMoreCompletedCases = useCallback(() => {
    if (!hasMoreCompletedCases || completedQuery.isFetchingNextPage) return;
    void completedQuery.fetchNextPage();
  }, [completedQuery, hasMoreCompletedCases]);

  const attachCompletedLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      completedBoundaryObserver.current?.disconnect();
      if (
        !node ||
        activeTab !== "completed" ||
        !hasMoreCompletedCases ||
        completedQuery.isFetchingNextPage ||
        completedLoading ||
        typeof IntersectionObserver === "undefined"
      )
        return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleRevealMoreCompletedCases();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      completedBoundaryObserver.current = observer;
      observer.observe(node);
    },
    [
      activeTab,
      hasMoreCompletedCases,
      completedQuery.isFetchingNextPage,
      completedLoading,
      handleRevealMoreCompletedCases,
    ]
  );

  const handleAllViewChange = useCallback(
    (nextView: PathologistQueueTabView) => {
      updateQueueViewTab("all", () => nextView);
    },
    [updateQueueViewTab]
  );

  const handleSentOutViewChange = useCallback(
    (nextView: PathologistQueueTabView) => {
      updateQueueViewTab("sentOut", () => nextView);
    },
    [updateQueueViewTab]
  );

  const handleClearSentOutView = useCallback(
    () => clearQueueViewTab("sentOut"),
    [clearQueueViewTab]
  );

  const handleClearAllView = useCallback(
    () => clearQueueViewTab("all"),
    [clearQueueViewTab]
  );

  const handleCorrectionsViewChange = useCallback(
    (nextView: PathologistQueueTabView) => {
      updateQueueViewTab("corrections", () => nextView);
    },
    [updateQueueViewTab]
  );

  const handleClearCorrectionsView = useCallback(
    () => clearQueueViewTab("corrections"),
    [clearQueueViewTab]
  );

  const handleClearInvalidActiveView = useCallback(() => {
    if (
      activeTab === "review" ||
      activeTab === "incoming" ||
      activeTab === "sentOut" ||
      activeTab === "all" ||
      activeTab === "completed" ||
      activeTab === "corrections"
    ) {
      clearQueueViewTab(activeTab);
    }
  }, [activeTab, clearQueueViewTab]);

  return (
    <Container fluid py="xs">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {activeViewInvalid && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="Saved queue view needs to be cleared"
            role="alert"
            mb="sm"
          >
            <Group justify="space-between" align="center">
              <Text size="sm">
                This saved view is no longer valid. Clear it to load the queue
                safely.
              </Text>
              <Button
                size="compact-sm"
                variant="light"
                color="red"
                onClick={handleClearInvalidActiveView}
              >
                Clear saved view
              </Button>
            </Group>
          </Alert>
        )}
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          variant="default"
          color="blue"
          styles={{
            list: {
              borderBottom: "1px solid var(--mantine-color-gray-3)",
              marginBottom: "var(--mantine-spacing-md)",
              gap: 0,
            },
            tab: {
              color: "var(--mantine-color-gray-6)",
              fontWeight: 400,
              fontSize: 14,
              padding: "8px 20px",
              borderRadius: 0,
              background: "none",
            },
            tabLabel: {
              fontWeight: "inherit",
            },
          }}
        >
          <Tabs.List>
            <Tabs.Tab
              value="review"
              leftSection={<IconStethoscope size={15} />}
              styles={{
                tab:
                  activeTab === "review"
                    ? {
                        color: "var(--mantine-color-blue-6)",
                        fontWeight: 600,
                      }
                    : {},
              }}
            >
              Pathologist Work Queue
              {queueStats?.total ? ` (${queueStats.total})` : ""}
            </Tabs.Tab>
            <Tabs.Tab
              value="corrections"
              leftSection={<IconAlertTriangle size={15} />}
              styles={{
                tab:
                  activeTab === "corrections"
                    ? {
                        color: "var(--mantine-color-orange-6)",
                        fontWeight: 600,
                      }
                    : {},
              }}
            >
              Correction Requests
              {correctionCount > 0 && (
                <Badge size="sm" variant="filled" color="orange" ml="xs">
                  {correctionCount}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab
              value="incoming"
              leftSection={<IconFlask size={15} />}
              styles={{
                tab:
                  activeTab === "incoming"
                    ? {
                        color: "var(--mantine-color-blue-6)",
                        fontWeight: 600,
                      }
                    : {},
              }}
            >
              Incoming Cases
              {(incomingData?.counts.total ?? 0) > 0 && (
                <Badge size="sm" variant="filled" color="blue" ml="xs">
                  {incomingData?.counts.total}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab
              value="sentOut"
              leftSection={<IconSend size={15} />}
              styles={{
                tab:
                  activeTab === "sentOut"
                    ? { color: "var(--mantine-color-blue-6)", fontWeight: 600 }
                    : {},
              }}
            >
              Sent Out
              {sentOutCount ? ` (${sentOutCount})` : ""}
            </Tabs.Tab>
            <Tabs.Tab value="qc">QC Slide Review</Tabs.Tab>
            {hasMedicalDirectorAccess && (
              <Tabs.Tab
                value="all"
                leftSection={<IconUsers size={15} />}
                styles={{
                  tab:
                    activeTab === "all"
                      ? {
                          color: "var(--mantine-color-blue-6)",
                          fontWeight: 600,
                        }
                      : {},
                }}
              >
                All Review Cases
              </Tabs.Tab>
            )}
            {isPathologist && (
              <Tabs.Tab
                value="completed"
                leftSection={<IconCircleCheck size={15} />}
                styles={{
                  tab:
                    activeTab === "completed"
                      ? {
                          color: "var(--mantine-color-teal-6)",
                          fontWeight: 600,
                        }
                      : {},
                }}
              >
                My Completed Cases
                {completedTotal > 0 && (
                  <Badge size="sm" variant="filled" color="teal" ml="xs">
                    {completedTotal}
                  </Badge>
                )}
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* ===== REVIEW TAB ===== */}
          <Tabs.Panel value="qc" pt="sm">
            <QcSlideReviewQueue active={activeTab === "qc"} />
          </Tabs.Panel>
          <Tabs.Panel value="review" pt="sm">
            <Stack gap="sm">
              {/* Summary chips + Refresh + Settings on same row */}
              <Group
                gap="xs"
                wrap="nowrap"
                align="flex-start"
                style={{ width: "100%" }}
              >
                <SummaryBar
                  stats={queueStats}
                  activeSummaryFilter={summaryFilter}
                  onStatClick={handleSummaryStatClick}
                  onClear={clearSummaryFilter}
                />
                <Group
                  gap="xs"
                  wrap="nowrap"
                  style={{ flexShrink: 0, marginLeft: "auto" }}
                >
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => refetch()}
                    loading={isFetching}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconFlask size={14} />}
                    onClick={handleDistributionNavigate}
                  >
                    Control Slides
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    size="md"
                    onClick={handleOpenPrefs}
                    title="Queue Preferences"
                  >
                    <IconSettings size={20} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* Barcode Scanner and Filters Row */}
              <Group align="flex-start" grow>
                {/* Barcode Scanner Input */}
                <Paper p="xs" withBorder style={{ flex: 2 }}>
                  <Group gap="xs">
                    <IconBarcode size={18} />
                    <TextInput
                      placeholder="Scan or enter barcode"
                      value={barcodeInput}
                      onChange={handleBarcodeChange}
                      onKeyPress={handleBarcodeKeyPress}
                      style={{ flex: 1 }}
                      size="sm"
                      autoFocus
                      error={error}
                    />
                    <Button
                      size="sm"
                      onClick={handleBarcodeSubmit}
                      loading={openCaseMutation.isPending}
                      disabled={!barcodeInput.trim()}
                    >
                      Open Case
                    </Button>
                    {(hasActiveFilters || hasActiveInlineFilters) && (
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleResetReviewFilters}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Group>
                </Paper>

                {/* Queue Filters */}
                <Paper p="xs" withBorder style={{ flex: 1 }}>
                  <Group gap="xs" wrap="wrap">
                    <>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={openFilterModal}
                        leftSection={<IconFilter size={14} />}
                      >
                        {hasActiveFilters ? "Edit Filters" : "Filters"}
                      </Button>
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleSaveDefault}
                        loading={defaultSaving}
                        disabled={
                          !queueViewHydrated ||
                          !defaultHydrated ||
                          defaultLoadError
                        }
                      >
                        Save Default
                      </Button>
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleApplyDefault}
                        disabled={
                          !queueViewHydrated ||
                          !defaultHydrated ||
                          !savedDefault
                        }
                      >
                        Apply Default
                      </Button>
                      {prefixOptions.length > 0 && (
                        <Checkbox.Group
                          label="Case prefixes"
                          value={filters.prefixKeys ?? []}
                          onChange={handlePrefixKeysChange}
                        >
                          <Group gap="xs" mt={4}>
                            {prefixOptions.map((prefixKey) => (
                              <Checkbox
                                key={prefixKey}
                                value={prefixKey}
                                label={
                                  prefixKey === "__OTHER__"
                                    ? "Other / nonstandard number"
                                    : prefixKey
                                }
                                size="xs"
                              />
                            ))}
                          </Group>
                        </Checkbox.Group>
                      )}
                      {prefixOptionsError && (
                        <Alert
                          color="red"
                          variant="light"
                          role="alert"
                          title="Prefixes unavailable"
                        >
                          The prefix choices could not be loaded.
                        </Alert>
                      )}
                      <Text size="xs" c="dimmed">
                        Defaults exclude patient selections and free-text
                        search.
                      </Text>
                      {(defaultError ||
                        defaultLoadError ||
                        (rawDefault && !savedDefault)) && (
                        <Alert
                          color="red"
                          variant="light"
                          role="alert"
                          title="Default unavailable"
                        >
                          {defaultError ??
                            "The saved default could not be loaded or is no longer valid."}
                        </Alert>
                      )}
                      {defaultMessage && (
                        <Text size="xs" role="status">
                          {defaultMessage}
                        </Text>
                      )}
                      {hasActiveFilters && (
                        <ActiveFilters
                          filters={filters}
                          prefixOptions={prefixOptions}
                          onFiltersChange={handleReviewFiltersChange}
                          onClearAll={handleClearAllFilters}
                        />
                      )}
                    </>
                  </Group>
                </Paper>
              </Group>

              {/* Active Cases Table */}
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <PathologistQueueColorLegend />
                  {queueError && (
                    <Alert
                      color="red"
                      variant="light"
                      role="alert"
                      title="Queue unavailable"
                    >
                      The work queue could not be refreshed. Your current rows
                      remain visible if available.
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleRetryReviewQueue}
                      >
                        Try again
                      </Button>
                    </Alert>
                  )}
                  {isLoading ? (
                    <Group justify="center" p="xl">
                      <Loader />
                      <Text role="status">Loading work queue</Text>
                    </Group>
                  ) : queueError && !queueCases ? null : activeCases.length ===
                    0 ? (
                    <Stack align="center" gap="sm" py="xl">
                      <IconInbox
                        size={36}
                        color="var(--mantine-color-gray-5)"
                      />
                      <Text fw={500} ta="center">
                        {hasActiveFilters || hasActiveInlineFilters
                          ? "No cases match the current filters"
                          : "No cases to review. You are all caught up."}
                      </Text>
                      {(hasActiveFilters || hasActiveInlineFilters) && (
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={handleResetReviewFilters}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Stack>
                  ) : (
                    <Table.ScrollContainer
                      minWidth={reviewTableWidth}
                      type="native"
                    >
                      <Table striped highlightOnHover>
                        {renderTableHeader(reviewColumns)}
                        <Table.Tbody>
                          {revealedActiveCases.map((caseItem) =>
                            renderCaseRow(caseItem, false, reviewColumns)
                          )}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  )}
                  {!isLoading && sortedActiveCases.length > 0 && (
                    <Stack gap="xs" align="center">
                      <Text
                        size="sm"
                        c="dimmed"
                        role="status"
                        aria-live="polite"
                      >
                        {hasMoreActiveCases
                          ? `Showing ${revealedActiveCases.length} of ${activeReviewTotal} cases`
                          : `All ${activeReviewTotal} cases shown`}
                      </Text>
                      {hasMoreActiveCases && (
                        <>
                          <Box
                            ref={attachReviewLoadBoundary}
                            h={1}
                            aria-hidden="true"
                          />
                          <Button
                            size="sm"
                            variant="light"
                            onClick={handleRevealMoreReviewCases}
                          >
                            Load more cases
                          </Button>
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Paper>

              {/* Blocked Cases Table */}
              {blockedCases.length > 0 && (
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Group gap="sm">
                        <IconAlertTriangle
                          size={20}
                          color="var(--mantine-color-red-6)"
                        />
                        <Text fw={500} size="lg" c="red">
                          Blocked Cases ({blockedReviewTotal})
                        </Text>
                      </Group>
                    </Group>
                    <Text size="sm" c="dimmed">
                      These cases have reported errors and are blocked until the
                      issue is resolved.
                    </Text>
                    <Divider />
                    <Table.ScrollContainer
                      minWidth={reviewTableWidth}
                      type="native"
                    >
                      <Table striped>
                        {renderTableHeader(reviewColumns)}
                        <Table.Tbody>
                          {paginatedBlockedCases.map((caseItem) =>
                            renderCaseRow(caseItem, true, reviewColumns)
                          )}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                    <Stack gap="xs" align="center">
                      <Text size="sm" c="dimmed" role="status">
                        {hasMoreBlockedCases
                          ? `Showing ${paginatedBlockedCases.length} of ${blockedReviewTotal} blocked cases`
                          : `All ${blockedReviewTotal} blocked cases shown`}
                      </Text>
                      {hasMoreBlockedCases && (
                        <>
                          <Box
                            ref={attachBlockedLoadBoundary}
                            h={1}
                            aria-hidden="true"
                          />
                          <Button
                            size="sm"
                            variant="light"
                            onClick={handleRevealMoreBlockedCases}
                          >
                            Load more blocked cases
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>

          {/* ===== INCOMING IN LAB TAB ===== */}
          <Tabs.Panel value="incoming" pt="sm">
            <Stack gap="sm">
              {/* Stage badges + Refresh + Settings on same row */}
              <Group
                gap="xs"
                wrap="nowrap"
                align="center"
                style={{ width: "100%" }}
              >
                <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
                  {(incomingData?.counts.grossing ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "grossing"
                          ? "filled"
                          : "light"
                      }
                      color="grape"
                      leftSection={<IconScissors size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepGrossing}
                    >
                      Grossing: {incomingData?.counts.grossing ?? 0}
                    </Badge>
                  )}
                  {(incomingData?.counts.embedding ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "embedding"
                          ? "filled"
                          : "light"
                      }
                      color="orange"
                      leftSection={<IconCube size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepEmbedding}
                    >
                      Embedding: {incomingData?.counts.embedding ?? 0}
                    </Badge>
                  )}
                  {(incomingData?.counts.cutting ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "cutting"
                          ? "filled"
                          : "light"
                      }
                      color="blue"
                      leftSection={<IconCut size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepCutting}
                    >
                      Cutting: {incomingData?.counts.cutting ?? 0}
                    </Badge>
                  )}
                  {(incomingData?.counts.staining ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "staining"
                          ? "filled"
                          : "light"
                      }
                      color="teal"
                      leftSection={<IconDroplet size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepStaining}
                    >
                      Staining: {incomingData?.counts.staining ?? 0}
                    </Badge>
                  )}
                  {(incomingData?.counts.awaitingSendOut ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "awaiting_send_out"
                          ? "filled"
                          : "light"
                      }
                      color="violet"
                      leftSection={<IconSend size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepSendOut}
                    >
                      Awaiting Send-Out:{" "}
                      {incomingData?.counts.awaitingSendOut ?? 0}
                    </Badge>
                  )}
                  {(incomingData?.counts.pendingStains ?? 0) > 0 && (
                    <Badge
                      size="sm"
                      variant={
                        incomingFilters.currentStep === "pending_stains"
                          ? "filled"
                          : "light"
                      }
                      color="yellow"
                      leftSection={<IconTestPipe size={14} />}
                      style={{ cursor: "pointer" }}
                      onClick={handleIncomingStepPendingStains}
                    >
                      Awaiting Stains: {incomingData?.counts.pendingStains ?? 0}
                    </Badge>
                  )}
                  {incomingFilters.currentStep && (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      rightSection={<IconX size={11} />}
                      onClick={handleIncomingStepClear}
                    >
                      Clear
                    </Button>
                  )}
                </Group>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={handleRefreshIncoming}
                    loading={incomingFetching}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    size="md"
                    onClick={handleOpenIncomingPrefs}
                    title="Incoming Preferences"
                  >
                    <IconSettings size={20} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* Barcode Scanner and Filters Row */}
              <Group align="flex-start" grow>
                <Paper p="xs" withBorder style={{ flex: 2 }}>
                  <Group gap="xs">
                    <IconBarcode size={18} />
                    <TextInput
                      placeholder="Scan or enter barcode"
                      value={barcodeInput}
                      onChange={handleBarcodeChange}
                      onKeyPress={handleBarcodeKeyPress}
                      style={{ flex: 1 }}
                      size="sm"
                      error={error}
                    />
                    <Button
                      size="sm"
                      onClick={handleBarcodeSubmit}
                      loading={openCaseMutation.isPending}
                      disabled={!barcodeInput.trim()}
                    >
                      Open Case
                    </Button>
                  </Group>
                </Paper>
                <Paper p="xs" withBorder style={{ flex: 1 }}>
                  <Group gap="xs" wrap="wrap">
                    <>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={openIncomingFilterModal}
                        leftSection={<IconFilter size={14} />}
                      >
                        {hasActiveIncomingFilters ? "Edit Filters" : "Filters"}
                      </Button>
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleSaveIncomingDefault}
                        loading={incomingDefaultSaving}
                        disabled={
                          !queueViewHydrated ||
                          !hydratedTabs.incoming ||
                          errorTabs.incoming
                        }
                      >
                        Save Default
                      </Button>
                      <Button
                        size="compact-sm"
                        variant="subtle"
                        onClick={handleApplyIncomingDefault}
                        disabled={
                          !queueViewHydrated ||
                          !hydratedTabs.incoming ||
                          !savedIncomingDefault
                        }
                      >
                        Apply Default
                      </Button>
                      {incomingPrefixOptions.length > 0 && (
                        <Checkbox.Group
                          label="Case prefixes"
                          value={incomingFilters.prefixKeys ?? []}
                          onChange={handleIncomingPrefixChange}
                        >
                          <Group gap="xs" mt={4}>
                            {incomingPrefixOptions.map((key) => (
                              <Checkbox
                                key={key}
                                value={key}
                                size="xs"
                                label={
                                  key === "__OTHER__"
                                    ? "Other / nonstandard number"
                                    : key
                                }
                              />
                            ))}
                          </Group>
                        </Checkbox.Group>
                      )}
                      <Text size="xs" c="dimmed">
                        Defaults exclude patient selections and free-text
                        search.
                      </Text>
                      {(incomingDefaultError ||
                        errorTabs.incoming ||
                        (rawDefaults.incoming && !savedIncomingDefault)) && (
                        <Alert
                          color="red"
                          variant="light"
                          role="alert"
                          title="Default unavailable"
                        >
                          {incomingDefaultError ??
                            "The saved default could not be loaded or is no longer valid."}
                        </Alert>
                      )}
                      {incomingDefaultMessage && (
                        <Text size="xs" role="status">
                          {incomingDefaultMessage}
                        </Text>
                      )}
                      {hasActiveIncomingFilters && (
                        <ActiveFilters
                          filters={incomingFilters}
                          prefixOptions={incomingPrefixOptions}
                          onFiltersChange={handleIncomingFiltersChange}
                          onClearAll={handleClearIncomingFilters}
                        />
                      )}
                    </>
                  </Group>
                </Paper>
              </Group>

              {/* Incoming cases table */}
              <Paper p="md" withBorder>
                {incomingLoading ? (
                  <Group justify="center" p="xl">
                    <Loader />
                  </Group>
                ) : sortedIncomingCases.length === 0 ? (
                  <Group justify="center" py="xl">
                    <Paper
                      p="lg"
                      withBorder
                      radius="md"
                      style={{ maxWidth: 420, width: "100%" }}
                    >
                      <Stack align="center" gap="sm">
                        <IconInbox
                          size={36}
                          color="var(--mantine-color-gray-5)"
                        />
                        <Text fw={500} ta="center">
                          {hasActiveIncomingFilters ||
                          incomingFilters.currentStep
                            ? "No cases match the current filters"
                            : "No incoming cases in the lab right now."}
                        </Text>
                        {(hasActiveIncomingFilters ||
                          incomingFilters.currentStep) && (
                          <Button
                            size="compact-sm"
                            variant="subtle"
                            onClick={handleResetIncomingFilters}
                          >
                            Clear Filters
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  </Group>
                ) : (
                  <Stack gap="xs">
                    <Table.ScrollContainer
                      minWidth={incomingTableWidth}
                      type="native"
                    >
                      <Table striped highlightOnHover>
                        {renderIncomingTableHeader()}
                        <Table.Tbody>
                          {revealedIncomingCases.map((caseItem) =>
                            renderIncomingCaseRow(caseItem)
                          )}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                    <Stack gap="xs" align="center">
                      <Text
                        size="sm"
                        c="dimmed"
                        role="status"
                        aria-live="polite"
                      >
                        {hasMoreIncomingCases
                          ? `Showing ${revealedIncomingCases.length} of ${incomingTotal} cases`
                          : `All ${incomingTotal} cases shown`}
                      </Text>
                      {hasMoreIncomingCases && (
                        <>
                          <Box
                            ref={attachIncomingLoadBoundary}
                            h={1}
                            aria-hidden="true"
                          />
                          <Button
                            size="sm"
                            variant="light"
                            onClick={handleRevealMoreIncomingCases}
                          >
                            Load more cases
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Tabs.Panel>

          <AllPathologistCasesTab
            tab="sentOut"
            prefixOptions={sentOutEligiblePrefixKeys ?? []}
            active={activeTab === "sentOut"}
            hydrated={queueViewHydrated}
            invalid={invalidTabs.has("sentOut")}
            view={sentOutView}
            onViewChange={handleSentOutViewChange}
            onClearView={handleClearSentOutView}
            columns={sentOutColumns}
            overdueThresholdDays={preferences.overdueThresholdDays}
            renderHeader={renderTableHeader}
            renderRow={renderCaseRow}
          />

          {hasMedicalDirectorAccess && (
            <AllPathologistCasesTab
              prefixOptions={allEligiblePrefixKeys ?? []}
              active={activeTab === "all"}
              hydrated={queueViewHydrated}
              invalid={invalidTabs.has("all")}
              view={allView}
              onViewChange={handleAllViewChange}
              onClearView={handleClearAllView}
              columns={allReviewColumns}
              overdueThresholdDays={preferences.overdueThresholdDays}
              renderHeader={renderTableHeader}
              renderRow={renderCaseRow}
            />
          )}

          {/* ===== CORRECTION REQUESTS TAB ===== */}
          <CorrectionRequestsTab
            view={correctionsView}
            onViewChange={handleCorrectionsViewChange}
            onClearView={handleClearCorrectionsView}
            active={activeTab === "corrections"}
            hydrated={queueViewHydrated}
            invalid={invalidTabs.has("corrections")}
          />

          {/* ===== MY COMPLETED CASES TAB ===== */}
          {isPathologist && (
            <Tabs.Panel value="completed" pt="sm">
              <Stack gap="sm">
                {/* Header + Refresh */}
                <Group
                  gap="xs"
                  wrap="nowrap"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <Text fw={600} size="lg" style={{ flex: 1, minWidth: 0 }}>
                    My Completed Cases
                    {completedTotal > 0 && (
                      <Badge size="sm" variant="light" color="teal" ml="xs">
                        {completedTotal}
                      </Badge>
                    )}
                  </Text>
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={handleRefreshCompleted}
                    loading={completedFetching}
                    leftSection={<IconRefresh size={16} />}
                  >
                    Refresh
                  </Button>
                </Group>

                {/* Search + Filters Row (side by side, like Incoming) */}
                <Group align="flex-start" grow>
                  <Paper p="xs" withBorder style={{ flex: 2 }}>
                    <Group gap="xs">
                      <IconSearch size={18} />
                      <TextInput
                        placeholder="Search by case # or patient name"
                        value={completedFilters.search || ""}
                        onChange={handleCompletedSearchChange}
                        style={{ flex: 1 }}
                        size="sm"
                      />
                    </Group>
                  </Paper>
                  <Paper p="xs" withBorder style={{ flex: 1 }}>
                    <Group gap="xs" wrap="wrap">
                      <>
                        <Button
                          size="sm"
                          variant="light"
                          onClick={openCompletedFilterModal}
                          leftSection={<IconFilter size={14} />}
                        >
                          {hasActiveCompletedFilters
                            ? "Edit Filters"
                            : "Filters"}
                        </Button>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={handleSaveCompletedDefault}
                          loading={completedDefaultSaving}
                          disabled={
                            !queueViewHydrated ||
                            !hydratedTabs.completed ||
                            errorTabs.completed
                          }
                        >
                          Save Default
                        </Button>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={handleApplyCompletedDefault}
                          disabled={
                            !queueViewHydrated ||
                            !hydratedTabs.completed ||
                            !savedCompletedDefault
                          }
                        >
                          Apply Default
                        </Button>
                        {completedPrefixOptions.length > 0 && (
                          <Checkbox.Group
                            label="Case prefixes"
                            value={completedFilters.prefixKeys ?? []}
                            onChange={handleCompletedPrefixChange}
                          >
                            <Group gap="xs" mt={4}>
                              {completedPrefixOptions.map((key) => (
                                <Checkbox
                                  key={key}
                                  value={key}
                                  size="xs"
                                  label={
                                    key === "__OTHER__"
                                      ? "Other / nonstandard number"
                                      : key
                                  }
                                />
                              ))}
                            </Group>
                          </Checkbox.Group>
                        )}
                        <Text size="xs" c="dimmed">
                          Defaults exclude patient selections and free-text
                          search.
                        </Text>
                        {(completedDefaultError ||
                          errorTabs.completed ||
                          (rawDefaults.completed &&
                            !savedCompletedDefault)) && (
                          <Alert
                            color="red"
                            variant="light"
                            role="alert"
                            title="Default unavailable"
                          >
                            {completedDefaultError ??
                              "The saved default could not be loaded or is no longer valid."}
                          </Alert>
                        )}
                        {completedDefaultMessage && (
                          <Text size="xs" role="status">
                            {completedDefaultMessage}
                          </Text>
                        )}
                        {hasActiveCompletedFilters && (
                          <ActiveFilters
                            filters={completedFilters}
                            prefixOptions={completedPrefixOptions}
                            onFiltersChange={handleCompletedFiltersChange}
                            onClearAll={handleClearCompletedFilters}
                          />
                        )}
                      </>
                    </Group>
                  </Paper>
                </Group>

                {/* Completed cases table */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    {completedLoading ? (
                      <Group justify="center" p="xl">
                        <Loader />
                      </Group>
                    ) : sortedCompletedCases.length === 0 ? (
                      <Stack align="center" gap="sm" py="xl">
                        <IconInbox
                          size={36}
                          color="var(--mantine-color-gray-5)"
                        />
                        <Text fw={500} ta="center">
                          {hasActiveCompletedFilters
                            ? "No cases match the current filters"
                            : "You haven't signed off any cases yet."}
                        </Text>
                        {hasActiveCompletedFilters && (
                          <Button
                            size="compact-sm"
                            variant="subtle"
                            onClick={handleClearCompletedFilters}
                          >
                            Clear Filters
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <>
                        <Table.ScrollContainer
                          minWidth={completedTableWidth}
                          type="native"
                        >
                          <Table striped highlightOnHover>
                            {renderTableHeader(
                              completedVisibleColumns,
                              completedSorts,
                              handleCompletedSort
                            )}
                            <Table.Tbody>
                              {revealedCompletedCases.map((caseItem) =>
                                renderCaseRow(
                                  caseItem,
                                  false,
                                  completedVisibleColumns
                                )
                              )}
                            </Table.Tbody>
                          </Table>
                        </Table.ScrollContainer>
                        <Stack gap="xs" align="center">
                          <Text
                            size="sm"
                            c="dimmed"
                            role="status"
                            aria-live="polite"
                          >
                            {hasMoreCompletedCases
                              ? `Showing ${revealedCompletedCases.length} of ${completedTotal} cases`
                              : `All ${completedTotal} cases shown`}
                          </Text>
                          {hasMoreCompletedCases && (
                            <>
                              <Box
                                ref={attachCompletedLoadBoundary}
                                h={1}
                                aria-hidden="true"
                              />
                              <Button
                                size="sm"
                                variant="light"
                                onClick={handleRevealMoreCompletedCases}
                              >
                                Load more cases
                              </Button>
                            </>
                          )}
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>
          )}
        </Tabs>

        {/* Review Tab Filter Modal */}
        <FilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          allowedStatusNames={[
            "Pending Pathologist Review",
            "Pathologist Review",
            "Pending External Report",
          ]}
          pathologistFilterMode="assigned-user"
          prefixOptions={prefixOptions}
          showQueueFilters
        />

        {/* Incoming Tab Filter Modal */}
        <FilterModal
          opened={incomingFilterModalOpened}
          onClose={closeIncomingFilterModal}
          filters={incomingFilters}
          onApply={handleIncomingFiltersChange}
          showIncomingFilters
        />

        {/* My Completed Cases Tab Filter Modal */}
        <FilterModal
          opened={completedFilterModalOpened}
          onClose={closeCompletedFilterModal}
          filters={completedFilters}
          onApply={handleCompletedFiltersApply}
          allowedStatusNames={["Signed Off"]}
        />

        {/* Queue Preferences Modal */}
        <QueuePreferencesModal
          opened={prefsModalOpen}
          onClose={handleClosePrefs}
          currentPreferences={preferences}
          onSave={handleSavePrefs}
          isSaving={savePreferencesMutation.isPending}
        />

        {/* Incoming Preferences Modal */}
        <IncomingPreferencesModal
          opened={incomingPrefsModalOpen}
          onClose={handleCloseIncomingPrefs}
          currentPreferences={incomingPreferences}
          onSave={handleSaveIncomingPrefs}
          isSaving={savePreferencesMutation.isPending}
        />
      </Box>
    </Container>
  );
}
